<template>
  <div class="app-header">
    <div class="header-container">
      <div class="logo-container">
        <router-link to="/" class="logo-link">
          <i class="pi pi-shield text-3xl mr-2 text-primary"></i>
          <span class="logo-text">Shit-OTP</span>
        </router-link>
      </div>
      
      <div class="nav-container">
        <ul class="nav-menu">
          <li class="nav-item" v-for="item in menuItems" :key="item.label">
            <router-link :to="item.to" class="nav-link">
              <i :class="[item.icon, 'mr-2']"></i>
              <span>{{ item.label }}</span>
            </router-link>
          </li>
        </ul>
      </div>
      
      <div class="header-actions">
        <Button
          icon="pi pi-moon"
          @click="toggleDarkMode"
          class="p-button-rounded p-button-text mr-2 theme-toggle-btn"
          v-tooltip.bottom="'Toggle Dark Mode'"
        />
        
        <template v-if="isAuthenticated">
          <Menu ref="userMenu" :model="userMenuItems" :popup="true" />
          <Button
            @click="openUserMenu"
            class="p-button-text user-button"
            aria-label="User Menu"
          >
            <span class="user-email">{{ userEmail }}</span>
            <i class="pi pi-chevron-down ml-2 text-sm"></i>
          </Button>
        </template>
        
        <template v-else>
          <router-link to="/login">
            <Button label="Login" icon="pi pi-sign-in" class="p-button-outlined login-btn" />
          </router-link>
          <router-link to="/register">
            <Button label="Sign Up" icon="pi pi-user-plus" class="p-button ml-2 signup-btn" />
          </router-link>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

// PrimeVue components
import Menu from 'primevue/menu';
import Button from 'primevue/button';

export default {
  name: 'AppHeader',
  components: {
    Menu,
    Button
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const userMenu = ref(null);
    
    const isAuthenticated = computed(() => store.getters.isAuthenticated);
    const userEmail = computed(() => store.getters.getUser?.email || '');
    
    // Main menu items
    const menuItems = computed(() => {
      const items = [];
      
      if (!isAuthenticated.value) {
        items.push({
          label: 'Home',
          icon: 'pi pi-home',
          to: '/'
        });
      }
      
      if (isAuthenticated.value) {
        items.push({
          label: 'My OTP Tokens',
          icon: 'pi pi-key',
          to: '/tokens'
        });
      }
      
      return items;
    });
    
    // Open user menu
    const openUserMenu = (event) => {
      userMenu.value.toggle(event);
    };
    
    // Logout function
    const logout = async () => {
      try {
        await store.dispatch('logout');
        router.push('/login');
      } catch (error) {
        console.error('Logout error:', error);
      }
    };
    
    // User menu items - must be defined after logout function
    const userMenuItems = [
      {
        label: 'Profile',
        icon: 'pi pi-user-edit',
        command: () => router.push('/profile')
      },
      {
        separator: true
      },
      {
        label: 'Logout',
        icon: 'pi pi-sign-out',
        command: logout
      }
    ];
    
    // Toggle dark mode
    const toggleDarkMode = () => {
      document.body.classList.toggle('dark-mode');
      
      // Dark mode durumunu localStorage'a kaydet
      const isDarkMode = document.body.classList.contains('dark-mode');
      localStorage.setItem('darkMode', isDarkMode ? 'true' : 'false');
      
      // PrimeVue tema uyumu için
      if (isDarkMode) {
        document.documentElement.classList.add('my-app-dark');
      } else {
        document.documentElement.classList.remove('my-app-dark');
      }
    };
    
    // Check session on page load
    onMounted(async () => {
      await store.dispatch('fetchSession');
    });
    
    return {
      isAuthenticated,
      userEmail,
      menuItems,
      userMenuItems,
      userMenu,
      openUserMenu,
      toggleDarkMode
    };
  }
};
</script>

<style lang="scss" scoped>
.app-header {
  background-color: var(--surface-card);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  z-index: 999;
  border-bottom: 1px solid var(--surface-border-light, var(--surface-border));
  
  .header-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0.75rem 1.5rem;
  }
  
  .logo-container {
    .logo-link {
      display: flex;
      align-items: center;
      text-decoration: none;
      
      i {
        font-size: 1.75rem;
      }
      
      .logo-text {
        font-size: 1.5rem;
        font-weight: 700;
        color: var(--text-color);
      }
      
      &:hover {
        .logo-text {
          color: var(--primary-color);
        }
      }
    }
  }
  
  .nav-container {
    display: flex;
    flex: 1;
    margin-left: 3rem;
    
    .nav-menu {
      display: flex;
      list-style: none;
      margin: 0;
      padding: 0;
      
      .nav-item {
        margin-right: 1.5rem;
        
        .nav-link {
          display: flex;
          align-items: center;
          padding: 0.5rem 0;
          color: var(--text-color-secondary);
          font-weight: 500;
          text-decoration: none;
          transition: all 0.2s ease;
          position: relative;
          
          &:hover, &.router-link-active {
            color: var(--primary-color);
          }
          
          &.router-link-active::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: 2px;
            background-color: var(--primary-color);
            border-radius: 1px;
          }
        }
      }
    }
  }
  
  .header-actions {
    display: flex;
    align-items: center;
    
    .theme-toggle-btn {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      color: var(--text-color-secondary);
      
      &:hover {
        background-color: var(--surface-hover);
      }
    }
    
    .user-button {
      display: flex;
      align-items: center;
      padding: 0.5rem 1rem;
      border-radius: 10px;
      color: var(--text-color);
      
      &:hover {
        background-color: var(--surface-hover);
      }
      
      .user-email {
        font-weight: 500;
        max-width: 150px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
    
    .login-btn, .signup-btn {
      border-radius: 10px;
      padding: 0.5rem 1.25rem;
      font-weight: 600;
      transition: all 0.2s ease;
      
      &:hover {
        transform: translateY(-2px);
      }
    }
    
    .signup-btn {
      box-shadow: 0 4px 12px rgba(var(--primary-color-rgb, 59, 130, 246), 0.25);
      
      &:hover {
        box-shadow: 0 6px 16px rgba(var(--primary-color-rgb, 59, 130, 246), 0.3);
      }
    }
  }
}

:deep(.p-menu) {
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  min-width: 200px;
  
  .p-menuitem {
    .p-menuitem-link {
      padding: 0.75rem 1rem;
      
      &:hover {
        background-color: var(--surface-hover);
      }
      
      .p-menuitem-text {
        color: var(--text-color);
        font-weight: 500;
      }
      
      .p-menuitem-icon {
        color: var(--text-color-secondary);
      }
    }
  }
}

@media (max-width: 767px) {
  .app-header {
    .nav-container {
      display: none;
    }
    
    .header-actions {
      .login-btn, .signup-btn {
        padding: 0.5rem 1rem;
        
        span {
          display: none;
        }
        
        i {
          margin-right: 0;
        }
      }
      
      .user-button {
        .user-email {
          display: none;
        }
      }
    }
  }
}

@media (max-width: 480px) {
  .app-header {
    .header-container {
      padding: 0.75rem 1rem;
    }
    
    .logo-container {
      .logo-link {
        .logo-text {
          font-size: 1.2rem;
        }
      }
    }
    
    .header-actions {
      .login-btn, .signup-btn {
        width: 40px;
        height: 40px;
        padding: 0;
        border-radius: 50%;
        
        span {
          display: none;
        }
      }
      
      .theme-toggle-btn {
        margin-right: 0.5rem;
      }
      
      .signup-btn {
        margin-left: 0.5rem !important;
      }
    }
  }
}
</style> 